import {useCallback, useRef} from 'react';

import {observeCriticalWorkflowPainWindow} from './painWindow/observer';
import type {
  CriticalWorkflowContext,
  CriticalWorkflowInitiatingEvent,
} from './painWindow/types';

interface CriticalWorkflowInteractionEvent {
  type?: unknown;
  timeStamp?: unknown;
  nativeEvent?: unknown;
}

interface ObserveCriticalWorkflowInteractionOptions {
  workflow: string;
  windowDurationMs?: number;
  event?: unknown;
  workflowContext?: CriticalWorkflowContext;
}

export interface CriticalWorkflowTriggerOptions {
  windowDurationMs?: number;
}

/**
 * Event Timing records browser-native event names and timestamps. React often
 * wraps those values, while React Aria's later onPress callback omits them.
 */
export function getCriticalWorkflowInitiatingEvent(
  event: unknown
): CriticalWorkflowInitiatingEvent | undefined {
  if (!isInteractionEvent(event)) {
    return undefined;
  }

  const candidate = isInteractionEvent(event.nativeEvent)
    ? event.nativeEvent
    : event;
  if (
    typeof candidate.type !== 'string' ||
    candidate.type.length === 0 ||
    typeof candidate.timeStamp !== 'number' ||
    !Number.isFinite(candidate.timeStamp)
  ) {
    return undefined;
  }

  return {name: candidate.type, startTime: candidate.timeStamp};
}

export function observeCriticalWorkflowInteraction({
  workflow,
  windowDurationMs,
  event,
  workflowContext,
}: ObserveCriticalWorkflowInteractionOptions): boolean {
  const initiatingEvent = getCriticalWorkflowInitiatingEvent(event);
  return observeCriticalWorkflowPainWindow({
    workflow,
    ...(windowDurationMs === undefined ? {} : {windowDurationMs}),
    ...(initiatingEvent == null ? {} : {initiatingEvent}),
    ...(workflowContext == null ? {} : {workflowContext}),
  });
}

export function useCriticalWorkflowTrigger(
  workflow: string,
  {windowDurationMs}: CriticalWorkflowTriggerOptions = {}
): {
  captureInitiatingEvent: (event: unknown) => void;
  observe: (workflowContext?: CriticalWorkflowContext) => boolean;
} {
  const initiatingEventRef = useRef<
    CriticalWorkflowInitiatingEvent | undefined
  >(undefined);

  const captureInitiatingEvent = useCallback((event: unknown) => {
    initiatingEventRef.current = getCriticalWorkflowInitiatingEvent(event);
  }, []);

  const observe = useCallback(
    (workflowContext?: CriticalWorkflowContext) => {
      const initiatingEvent = initiatingEventRef.current;
      initiatingEventRef.current = undefined;
      return observeCriticalWorkflowPainWindow({
        workflow,
        ...(windowDurationMs === undefined ? {} : {windowDurationMs}),
        ...(initiatingEvent == null ? {} : {initiatingEvent}),
        ...(workflowContext == null ? {} : {workflowContext}),
      });
    },
    [windowDurationMs, workflow]
  );

  return {captureInitiatingEvent, observe};
}

function isInteractionEvent(
  event: unknown
): event is CriticalWorkflowInteractionEvent {
  return typeof event === 'object' && event != null;
}
